import React, { useMemo } from "react";
import { Box, Typography, makeStyles, IconButton, Checkbox, FormControlLabel, CircularProgress } from "@material-ui/core";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import AppInput from "components/Input/AppInput";
import SelectField from "components/Select/SelectField";
import CustomDatePicker from "components/Input/CustomDatePicker";
import AppColor from "utilities/AppColor";
import { createClientGoal, updateClientGoal } from "store/slices/goalTracker";
import { NDIS_SUPPORT_CATEGORY, MEASUREMENT_METHOD, GOAL_STATUS, SUPPORT_CATEGORY_SUBCATEGORY } from "config/AppConfig";
import moment from "moment";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: AppColor.mainColor,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
    gap: 8,
  },
  milestoneRow: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    marginBottom: 10,
  }
}));

const subCategoryOptions = [
  { value: SUPPORT_CATEGORY_SUBCATEGORY.DAILY_ACTIVITIES, label: "Daily Activities" },
  { value: SUPPORT_CATEGORY_SUBCATEGORY.COMMUNITY_PARTICIPATION, label: "Community Participation" },
  { value: SUPPORT_CATEGORY_SUBCATEGORY.SIL, label: "SIL" }
];

const AddGoalModal = ({ open, onClose, clientId, onSuccess, initialData, questions = [] }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isEditMode = Boolean(initialData?.id);

  const categoryOptions = [
    { value: NDIS_SUPPORT_CATEGORY.CORE_SUPPORTS, label: t("goalCategory.CORE_SUPPORTS", "Core Supports") },
    { value: NDIS_SUPPORT_CATEGORY.CAPACITY_BUILDING, label: t("goalCategory.CAPACITY_BUILDING", "Capacity Building") },
    { value: NDIS_SUPPORT_CATEGORY.CAPITAL_SUPPORTS, label: t("goalCategory.CAPITAL_SUPPORTS", "Capital Supports") },
  ];

  const measurementOptions = [
    { value: MEASUREMENT_METHOD.MILESTONE, label: t("goalMeasurement.MILESTONE", "Milestone") },
    { value: MEASUREMENT_METHOD.PERCENTAGE, label: t("goalMeasurement.PERCENTAGE", "Percentage") },
    { value: MEASUREMENT_METHOD.FREQUENCY_COUNT, label: t("goalMeasurement.FREQUENCY_COUNT", "Frequency Count") },
    { value: MEASUREMENT_METHOD.OBSERVATION, label: t("goalMeasurement.OBSERVATION", "Observation") },
  ];

  const initialValues = {
    name: initialData?.name || "",
    description: initialData?.description || "",
    category: initialData?.category || "",
    subCategory: initialData?.subCategory || "",
    measurementMethod: initialData?.measurementMethod || "",
    targetValue: initialData?.targetValue || "",
    progressPercentage: initialData?.progressPercentage ?? "",
    targetDate: initialData?.targetDate ? moment(initialData.targetDate).format("YYYY/MM/DD") : "",
    questionId: initialData?.questionId || "",
    status: initialData?.status || GOAL_STATUS.DRAFT,
    milestones: initialData?.milestones?.length 
      ? initialData.milestones 
      : (initialData?.measurementMethod === MEASUREMENT_METHOD.MILESTONE ? [{ name: "", isCompleted: false }] : []),
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required(t("validation.required")).max(255, t("validation.max_length", { value: 255 })),
    description: Yup.string().max(1000, t("validation.max_length", { value: 1000 })),
    category: Yup.string().required(t("validation.required")),
    subCategory: Yup.string().required(t("validation.required")),
    measurementMethod: Yup.string().required(t("validation.required")),
    targetDate: Yup.string().when("status", {
      is: GOAL_STATUS.DRAFT,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required(t("validation.required"))
    }),
    questionId: Yup.string().when("status", {
      is: (val) => val !== GOAL_STATUS.DRAFT,
      then: (schema) => schema.required(t("validation.required"))
    }),
    targetValue: Yup.number().when("measurementMethod", {
      is: MEASUREMENT_METHOD.FREQUENCY_COUNT,
      then: (schema) => schema.min(1, t("validation.min")).max(999999, t("validation.maxValue", { value: 999999 })).required(t("validation.required"))
    }),
    milestones: Yup.array().when("measurementMethod", {
      is: MEASUREMENT_METHOD.MILESTONE,
      then: (schema) => schema.min(1, t("validation.required")).of(
        Yup.object().shape({
          name: Yup.string().trim().required(t("validation.required")).max(255, t("validation.max_length", { value: 255 }))
        })
      )
    }),
    progressPercentage: Yup.number().when("measurementMethod", {
      is: (val) => val === MEASUREMENT_METHOD.PERCENTAGE && isEditMode,
      then: (schema) => schema.min(0, t("validation.min")).max(100, t("validation.max")).required(t("validation.required"))
    })
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      clientId,
      name: values.name.trim(),
      description: values.description,
      category: values.category,
      subCategory: values.subCategory || null,
      measurementMethod: values.measurementMethod,
      targetValue: values.measurementMethod === MEASUREMENT_METHOD.FREQUENCY_COUNT && values.targetValue ? Number(values.targetValue) : null,
      targetDate: values.targetDate ? moment(values.targetDate).format("YYYY/MM/DD") : null,
      questionIds: values.questionId ? [Number(values.questionId)] : [],
      status: values.status,
      progressPercentage: values.measurementMethod === MEASUREMENT_METHOD.PERCENTAGE && values.progressPercentage !== "" ? Number(values.progressPercentage) : null,
    };

    if (values.measurementMethod === MEASUREMENT_METHOD.MILESTONE && values.milestones) {
      if (isEditMode) {
        payload.milestones = values.milestones.map(m => ({
          id: m.id || null,
          name: m.name,
        })).filter(m => m.name?.trim());
      } else {
        payload.milestones = values.milestones
          .map(m => m.name || m)
          .filter(m => typeof m === 'string' ? m.trim() : m.name?.trim());
      }
    }

    let result;
    if (isEditMode) {
      result = await dispatch(updateClientGoal({ id: initialData.id, payload }));
    } else {
      result = await dispatch(createClientGoal(payload));
    }
    
    setSubmitting(false);
    if (!result.error) {
      onSuccess && onSuccess();
      onClose();
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={isEditMode ? t("goal.edit_title", "Edit Goal") : t("goal.add_title", "Add Goal")}
      maxWidth="md"
    >
      <Box>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, isSubmitting, errors }) => {

            const currentQuestionOptions = questions
              .filter(q => values.measurementMethod !== MEASUREMENT_METHOD.FREQUENCY_COUNT || q.questionType === "YES_NO")
              .map(q => ({
                value: q.id,
                label: q.question
              }));

            const isAchievedAllowed = values.measurementMethod === MEASUREMENT_METHOD.OBSERVATION ||
              (values.measurementMethod === MEASUREMENT_METHOD.PERCENTAGE && values.progressPercentage >= 100) ||
              (values.measurementMethod !== MEASUREMENT_METHOD.PERCENTAGE && initialData?.progressPercentage >= 100);

            const statusOptions = [];
            statusOptions.push({ value: GOAL_STATUS.DRAFT, label: t("goalStatus.DRAFT") });
            statusOptions.push({ value: GOAL_STATUS.ACTIVE, label: t("goalStatus.ACTIVE") });

            return (
              <Form style={{ position: "relative" }}>
                {isSubmitting && (
                  <Box position="absolute" top={0} left={0} right={0} bottom={0} bgcolor="rgba(255,255,255,0.6)" zIndex={10} display="flex" justifyContent="center" alignItems="center" borderRadius={8}>
                    <CircularProgress size={32} style={{ color: AppColor.mainColor }} />
                  </Box>
                )}
                <Typography className={classes.sectionTitle}>
                  {t("goal.section_details", "Goal Details")}
                </Typography>
                <div className="row">
                  <div className="col-sm-12 col-md-12">
                    <Field
                      component={AppInput}
                      name="name"
                      label="goal.name"
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="category"
                      label="goal.ndis_category"
                      options={categoryOptions}
                      required
                      onChange={(e) => {
                        setFieldValue("category", e.target.value);
                        setFieldValue("subCategory", "");
                      }}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="subCategory"
                      label="goal.subCategory"
                      options={subCategoryOptions}
                      required
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="measurementMethod"
                      label="goal.measurementMethod"
                      options={measurementOptions}
                      required
                      disabled={isEditMode && values.status !== GOAL_STATUS.DRAFT}
                      onSelected={(val) => {
                        setFieldValue("measurementMethod", val);
                        
                        if (val === MEASUREMENT_METHOD.MILESTONE && (!values.milestones || values.milestones.length === 0)) {
                          setFieldValue("milestones", [{ name: "", isCompleted: false }]);
                        }
                      }}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={CustomDatePicker}
                      name="targetDate"
                      label="goal.targetDate"
                      formatDateJob={true}
                      custom={{ disablePast: true }}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="questionId"
                      label="goal.linkedQuestionPrompt"
                      options={currentQuestionOptions}
                    />
                  </div>
                  <div className="col-sm-12 col-md-6">
                    <Field
                      component={SelectField}
                      name="status"
                      label="goal.status"
                      options={statusOptions}
                      disabled={!(!isEditMode || initialData?.latestObservation == null)}
                    />
                  </div>
                  {values.measurementMethod === MEASUREMENT_METHOD.FREQUENCY_COUNT && (
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={AppInput}
                        type="number"
                        name="targetValue"
                        label="goal.targetValue"
                        required
                      />
                    </div>
                  )}
                  {values.measurementMethod === MEASUREMENT_METHOD.PERCENTAGE && isEditMode && (
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={AppInput}
                        type="number"
                        name="progressPercentage"
                        label="goal.progressPercentage"
                        required
                      />
                    </div>
                  )}
                  <div className="col-sm-12 col-md-12">
                    <Field
                      component={AppInput}
                      name="description"
                      label="goal.description"
                      placeholder={t("goal.description_placeholder", "What does success look like?")}
                      multiline
                      type="textarea"
                      rows={3}
                    />
                  </div>
                </div>

                {values.measurementMethod === MEASUREMENT_METHOD.MILESTONE && (
                  <>
                    <Typography className={classes.sectionTitle}>
                      {t("goal.section_milestones", "Milestones")}
                    </Typography>
                    <FieldArray name="milestones">
                      {({ push, remove }) => (
                        <Box>
                          {values.milestones.map((m, index) => (
                            <Box key={index} className={classes.milestoneRow}>
                              <Box flex={1}>
                                <Field
                                  component={AppInput}
                                  name={`milestones.${index}.name`}
                                  placeholder={t("goal.milestone_name")}
                                />
                              </Box>
                              <Box style={{ height: 40, display: "flex", alignItems: "center" }}>
                                <IconButton 
                                  onClick={() => remove(index)} 
                                  color="secondary" 
                                  style={{ padding: 8 }}
                                  disabled={values.milestones.length <= 1}
                                >
                                  <i className="fas fa-trash-alt" style={{ fontSize: 16, opacity: values.milestones.length <= 1 ? 0.4 : 1 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                          <AppButton
                            label={t("goal.add_milestone")}
                            mode="outlined"
                            buttonColor={AppColor.mainColor}
                            onClick={() => push({ name: "", isCompleted: false })}
                            startIcon={<i className="fas fa-plus" />}
                          />
                        </Box>
                      )}
                    </FieldArray>
                  </>
                )}

                <Box className={classes.control}>
                  <AppButton
                    label={t("button.cancel")}
                    onClick={onClose}
                    buttonColor="#F3F4F6"
                    rounder
                    style={{ color: "#111827" }}
                    disabled={isSubmitting}
                  />
                  <AppButton
                    label={isEditMode ? t("button.save_changes") : t("button.save")}
                    rounder
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    buttonColor={AppColor.mainColor}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </AppDialog>
  );
};

export default AddGoalModal;
